// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { casesApi } from '../api/cases';
import type { CaseList, CaseSummary } from '../api/types';
import { useAuth } from '../app/AuthGate';
import { caseHref, formatBytes, nameFromFile } from '../app/cases';
import { useArmedConfirm } from '../app/useArmedConfirm';
import { useAsync } from '../app/useAsync';
import { useMutationQueue } from '../app/useMutationQueue';
import { Button } from '../components/Button';
import { ErrorBanner } from '../components/ErrorBanner';
import { Spinner } from '../components/Spinner';
import { TextInput } from '../components/TextInput';
import { browserStorage, pruneCaseSettings } from '../components/bygning/settings/settingsStorage';
import { CaseCard } from '../components/sager/CaseCard';
import { SagerWordmark } from '../components/sager/SagerLogo';
import controls from '../components/controls.module.css';
import { danishDate } from '../overblik/model';
import {
  cardFigures,
  cardFileLine,
  cardProgress,
  cardReuseText,
  cardSubline,
  cardTitle,
  caseThumbUrl,
  caseWriteErrorText,
  canDeleteCase,
  DELETE_CASE_CONFIRM_TEXT,
  deleteCaseClick,
  deleteCaseLabel,
  removeCase,
  SERVE_DIRECTORY_COMMAND,
  sortCases,
  uploadPercent,
} from '../sager/model';
import styles from './SagerPage.module.css';

/**
 * Sager — every case the server serves (R1, ruxd multi-case phase S2), with
 * create and upload. A card opens its case with a page load (`app/cases.ts`).
 */
export function SagerPage({ controls }: { controls: ReactNode }) {
  const list = useAsync<CaseList>((s) => casesApi.list(s), []);
  // Cheap GC: the 3D viewer's per-case settings of cases that are gone (frontend-38f).
  const user = useAuth()?.me.user.id ?? null;
  useEffect(() => {
    if (list.data) pruneCaseSettings(browserStorage(), user, list.data.cases.map((c) => c.id));
  }, [list.data, user]);
  const titleId = useId();
  // Cases deleted on this page, dropped from the list until the reload lands.
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const [deleteFailure, setDeleteFailure] = useState<{ cid: string; text: string } | null>(null);
  const failedCid = useRef<string | null>(null);
  const { busy, mutate } = useMutationQueue({
    scope: 'page',
    onError: (cause) => {
      if (failedCid.current) setDeleteFailure({ cid: failedCid.current, text: caseWriteErrorText(cause, 'delete') });
    },
  });
  const confirm = useArmedConfirm<string>(busy, null);

  const onDelete = (cid: string, button: HTMLElement) => {
    if (busy) return;
    if (deleteCaseClick(confirm.armed, cid) === 'arm') {
      setDeleteFailure(null);
      confirm.arm(cid, button);
      return;
    }
    confirm.disarm();
    failedCid.current = cid;
    void mutate(async () => {
      await removeCase(casesApi, list.data?.cases ?? [], cid);
      setRemoved((prev) => new Set(prev).add(cid));
      list.reload();
    });
  };

  if (list.error) {
    return (
      <Frame controls={controls}>
        <ErrorBanner error={list.error} onRetry={list.reload} context="sagslisten" />
      </Frame>
    );
  }
  if (list.loading && !list.data) {
    return (
      <Frame controls={controls}>
        <Spinner label="Indlæser sager…" />
      </Frame>
    );
  }
  if (!list.data) return null;

  const cases = sortCases(list.data.cases.filter((c) => !removed.has(c.id)));
  const { writable } = list.data;
  return (
    <Frame controls={controls}>
      <section className={styles.section} aria-labelledby={titleId}>
        <div className={styles.head}>
          {/* Under the big wordmark the page name is a section heading. */}
          <h1 id={titleId} className={styles.title}>
            Sager
          </h1>
          <span className={styles.count}>{cases.length}</span>
        </div>

        {cases.length === 0 && <p className={styles.muted}>Serveren har ingen sager endnu.</p>}
        {(cases.length > 0 || !writable) && (
          <ul className={styles.cards} aria-label="Sager">
            {cases.map((c) => (
              <li key={c.id}>
                <CaseTile
                  summary={c}
                  armed={confirm.armed === c.id}
                  busy={busy}
                  onDelete={onDelete}
                  onBlur={confirm.disarm}
                />
                {confirm.armed === c.id && (
                  <p className={styles.muted} role="status">
                    {DELETE_CASE_CONFIRM_TEXT}
                  </p>
                )}
                {deleteFailure?.cid === c.id && (
                  <p className={styles.error} role="alert">
                    {deleteFailure.text}
                  </p>
                )}
              </li>
            ))}
            {/* A server on one file can't take more: how to serve a directory, as the last tile. */}
            {!writable && (
              <li>
                <MoreCasesTile />
              </li>
            )}
          </ul>
        )}
      </section>

      {writable && <NewCasePanel list={list.data} />}
    </Frame>
  );
}

/** "Flere sager": a dashed tile the size of a card, for a server started on one file. */
function MoreCasesTile() {
  const id = useId();
  return (
    <section className={styles.moreTile} aria-labelledby={id}>
      <h2 id={id} className={styles.moreTitle}>
        Flere sager
      </h2>
      <p className={styles.muted}>Start serveren med en mappe — hver .rux i mappen bliver en sag:</p>
      <code className={styles.command}>{SERVE_DIRECTORY_COMMAND}</code>
    </section>
  );
}

/**
 * The page's column, headed by the big wordmark with the theme and user
 * controls (CasesBarControls) on its right — Sager has no bar of its own.
 */
function Frame({ controls, children }: { controls: ReactNode; children: ReactNode }) {
  return (
    <div className={styles.page}>
      <header className={styles.top}>
        <SagerWordmark />
        {controls}
      </header>
      {children}
    </div>
  );
}

/**
 * One card. Its figures come with the list (`Case.summary`, read by the
 * server without opening the case), so the page makes no request per case;
 * only the plan thumbnail is fetched, lazily, and the server caches it.
 */
function CaseTile({
  summary,
  armed,
  busy,
  onDelete,
  onBlur,
}: {
  summary: CaseSummary;
  armed: boolean;
  busy: boolean;
  onDelete: (cid: string, button: HTMLElement) => void;
  onBlur: () => void;
}) {
  const card = cardFigures(summary);
  return (
    <CaseCard
      href={caseHref(summary.id)}
      name={cardTitle(summary, card.record)}
      subline={card.unreadable ? 'Projektet kunne ikke læses' : cardSubline(card.record)}
      progress={card.survey ? cardProgress(card.survey) : null}
      reuseText={card.survey ? cardReuseText(card.survey) : null}
      status={card.status}
      date={summary.created_at ? `Oprettet ${danishDate(summary.created_at.slice(0, 10))}` : '—'}
      fileLine={cardFileLine(summary)}
      archived={summary.archived}
      thumbUrl={caseThumbUrl(summary.id)}
      actions={
        canDeleteCase(summary) && (
          <Button
            variant="outline"
            tone="danger"
            size="sm"
            armed={armed}
            disabled={busy}
            aria-label={armed ? `Bekræft: slet ${summary.name}` : `Slet ${summary.name}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(summary.id, e.currentTarget);
            }}
            onBlur={onBlur}
          >
            {deleteCaseLabel(armed)}
          </Button>
        )
      }
    />
  );
}

/** Create an empty case, or upload a `.rux` as one (a writable server only). */
function NewCasePanel({ list }: { list: CaseList }) {
  const id = useId();
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const action = useRef<'create' | 'upload'>('create');
  const abort = useRef<AbortController | null>(null);

  // Uploads run for minutes and change no survey state: a chain of their own.
  const { busy, mutate } = useMutationQueue({
    scope: 'page',
    onError: (cause) => {
      setProgress(null);
      setFailure(caseWriteErrorText(cause, action.current));
    },
  });

  const create = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    action.current = 'create';
    setFailure(null);
    void mutate(async () => {
      const created = await casesApi.create(trimmed);
      window.location.assign(caseHref(created.id));
    });
  };

  const upload = (e: FormEvent) => {
    e.preventDefault();
    if (!file || busy) return;
    const caseName = uploadName.trim() || nameFromFile(file.name) || file.name;
    action.current = 'upload';
    setFailure(null);
    const controller = new AbortController();
    abort.current = controller;
    setProgress({ sent: 0, total: file.size });
    void mutate(async () => {
      const created = await casesApi.uploadFile(file, caseName, setProgress, controller.signal);
      window.location.assign(caseHref(created.id));
    });
  };

  const tooLarge = file !== null && file.size > list.upload.max_bytes;
  return (
    <section className={styles.panel} aria-labelledby={`${id}-new`}>
      <div className={styles.columns}>
        <div className={styles.column}>
          <h2 id={`${id}-new`} className={styles.panelHeading}>
            Ny sag
          </h2>
          <form className={styles.row} onSubmit={create}>
            <label className={controls.field}>
              <span className={controls.fieldLabel}>Navn</span>
              <TextInput
                variant="outline"
                size="sm"
                block
                value={name}
                maxLength={200}
                placeholder="fx Rådhusstræde 4"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <Button type="submit" variant="primary" size="sm" disabled={busy || name.trim() === ''}>
              Opret tom sag
            </Button>
          </form>
        </div>

        <div className={styles.column}>
          <h3 className={styles.subHeading}>Upload en projektfil</h3>
          <form className={styles.row} onSubmit={upload}>
            <label className={controls.field}>
              <span className={controls.fieldLabel}>Fil (.rux)</span>
              {/* The native picker's own copy is in the browser's language; this
                  one speaks Danish and shows what was picked. */}
              <span className={styles.filePick}>
                <input
                  className={styles.fileInput}
                  type="file"
                  accept=".rux"
                  disabled={busy}
                  onChange={(e) => {
                    const picked = e.target.files?.[0] ?? null;
                    setFile(picked);
                    if (picked) setUploadName(nameFromFile(picked.name));
                  }}
                />
                <span className={styles.fileButton} aria-hidden="true">
                  Vælg fil…
                </span>
                <span className={styles.fileName}>
                  {file ? `${file.name} · ${formatBytes(file.size)}` : 'Ingen fil valgt'}
                </span>
              </span>
            </label>
            <label className={controls.field}>
              <span className={controls.fieldLabel}>Navn</span>
              <TextInput
                variant="outline"
                size="sm"
                block
                value={uploadName}
                maxLength={200}
                disabled={!file}
                onChange={(e) => setUploadName(e.target.value)}
              />
            </label>
            {progress ? (
              <Button variant="outline" size="sm" onClick={() => abort.current?.abort()}>
                Afbryd
              </Button>
            ) : (
              <Button type="submit" variant="primary" size="sm" disabled={busy || !file || tooLarge}>
                Upload
              </Button>
            )}
          </form>

          {progress && (
            <div className={styles.progress}>
              <progress className={styles.bar} max={progress.total} value={progress.sent} />
              <span className="mono">
                {uploadPercent(progress.sent, progress.total)} · {formatBytes(progress.sent)} af{' '}
                {formatBytes(progress.total)}
              </span>
            </div>
          )}
          {tooLarge && (
            <p className={styles.error} role="alert">
              Filen er {formatBytes(file.size)}; serveren tager højst imod {formatBytes(list.upload.max_bytes)}.
            </p>
          )}
          <p className={styles.muted}>
            Filen sendes i bidder og bliver en ny sag på serveren. Originalen på din maskine røres ikke.
          </p>
        </div>
      </div>

      {/* Either action's failure (create or upload), under both columns. */}
      {failure && (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}
    </section>
  );
}
